import React, { useState, useEffect } from 'react';
import {
  Database, Mail, User, Key, Hash, Globe, Server, Users, Cog, Shield,
  AlertTriangle, CheckCircle, XCircle, RefreshCw, Loader2, ChevronDown,
  ChevronUp, ExternalLink, Search, Filter, Download, Eye, EyeOff, List
} from 'lucide-react';
import { osint as osintApi, scans as scansApi } from '../api/client';
import { toast } from '../utils/toastUtils';

// Data type icons mapping
const TYPE_ICONS = {
  email: Mail,
  username: User,
  password: Key,
  hash: Hash,
  domain: Globe,
  ip: Server,
  ad_account: Users,
  service_account: Cog,
  api_key: Key,
  certificate: Shield
};

// Data type colors
const TYPE_COLORS = {
  email: 'blue',
  username: 'purple',
  password: 'red',
  hash: 'orange',
  domain: 'green',
  ip: 'cyan',
  ad_account: 'yellow',
  service_account: 'gray',
  api_key: 'pink',
  certificate: 'teal'
};

// Risk score badge
const RiskBadge = ({ score }) => {
  let color = 'bg-green-500/10 text-green-400 border-green-500/30';
  let label = 'Faible';

  if (score >= 50) {
    color = 'bg-red-500/10 text-red-400 border-red-500/30';
    label = 'Critique';
  } else if (score >= 20) {
    color = 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
    label = 'Moyen';
  }

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border ${color}`}>
      {score} - {label}
    </span>
  );
};

// Single credential card
const CredentialCard = ({ credential, dataType, showDetails, onToggleDetails }) => {
  const Icon = TYPE_ICONS[dataType] || Database;
  const color = TYPE_COLORS[dataType] || 'gray';
  const osintResults = credential.osint_results || {};

  // Check if breached
  const hibpData = osintResults.hibp || {};
  const isBreached = hibpData.breach_count > 0 || hibpData.breaches?.length > 0;
  const breachCount = hibpData.breach_count || hibpData.breaches?.length || 0;

  return (
    <div className={`border rounded-lg p-3 bg-slate-800/50 border-${color}-500/30 hover:border-${color}-500/50 transition-colors`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-${color}-500/10`}>
            <Icon className={`w-4 h-4 text-${color}-400`} />
          </div>
          <div>
            <p className="font-medium text-white text-sm">{credential.username}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <RiskBadge score={credential.risk_score || 0} />
              {isBreached && (
                <span className="text-xs text-red-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {breachCount} breach{breachCount > 1 ? 'es' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => onToggleDetails(credential.id)}
          className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
        >
          {showDetails ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
      </div>

      {/* Details section */}
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
          {/* HIBP Results */}
          {osintResults.hibp && (
            <div className="bg-slate-900/50 rounded-lg p-2">
              <p className="text-xs font-medium text-gray-400 mb-1">HIBP Breaches</p>
              {isBreached ? (
                <div className="space-y-1">
                  {(hibpData.breaches || []).slice(0, 5).map((breach, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-red-400">{breach.Name || breach}</span>
                      {breach.BreachDate && (
                        <span className="text-gray-500">{breach.BreachDate}</span>
                      )}
                    </div>
                  ))}
                  {(hibpData.breaches || []).length > 5 && (
                    <p className="text-xs text-gray-500">
                      +{(hibpData.breaches || []).length - 5} autres breaches
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Aucune breach détectée
                </p>
              )}
            </div>
          )}

          {/* Maigret Results */}
          {osintResults.maigret && (
            <div className="bg-slate-900/50 rounded-lg p-2">
              <p className="text-xs font-medium text-gray-400 mb-1">Comptes trouvés (Maigret)</p>
              <p className="text-xs text-purple-400">
                {osintResults.maigret.account_count || 0} comptes sur différentes plateformes
              </p>
              {osintResults.maigret.sites?.slice(0, 3).map((site, idx) => (
                <span key={idx} className="text-xs text-gray-500 mr-2">{site}</span>
              ))}
            </div>
          )}

          {/* Password Check */}
          {osintResults.password_check && (
            <div className="bg-slate-900/50 rounded-lg p-2">
              <p className="text-xs font-medium text-gray-400 mb-1">Mot de passe</p>
              {osintResults.password_check.pwned ? (
                <p className="text-xs text-red-400 flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  Compromis ({osintResults.password_check.count?.toLocaleString() || 'N/A'} occurrences)
                </p>
              ) : (
                <p className="text-xs text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Non compromis
                </p>
              )}
            </div>
          )}

          {credential.enriched_at && (
            <p className="text-xs text-gray-500">
              Enrichi le {new Date(credential.enriched_at).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Data type section
const DataTypeSection = ({ dataType, typeInfo, credentials, expandedCredentials, onToggleDetails }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const Icon = TYPE_ICONS[dataType] || Database;
  const color = TYPE_COLORS[dataType] || 'gray';

  const breachedCount = credentials.filter(c => {
    const hibp = c.osint_results?.hibp || {};
    return hibp.breach_count > 0 || hibp.breaches?.length > 0;
  }).length;

  const highRiskCount = credentials.filter(c => (c.risk_score || 0) >= 50).length;

  return (
    <div className={`border rounded-xl bg-slate-800/30 border-${color}-500/20`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg bg-${color}-500/10`}>
            <Icon className={`w-5 h-5 text-${color}-400`} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-white">{typeInfo.name || dataType}</h3>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-sm text-gray-400">{credentials.length} elements</span>
              {breachedCount > 0 && (
                <span className="text-sm text-red-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {breachedCount} breach{breachedCount > 1 ? 'es' : ''}
                </span>
              )}
              {highRiskCount > 0 && (
                <span className="text-sm text-yellow-400">
                  {highRiskCount} haut risque
                </span>
              )}
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Credentials list */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-2 max-h-96 overflow-y-auto">
          {credentials.length === 0 ? (
            <p className="text-gray-500 text-sm py-4 text-center">Aucun element</p>
          ) : (
            credentials.map(cred => (
              <CredentialCard
                key={cred.id}
                credential={cred}
                dataType={dataType}
                showDetails={expandedCredentials.has(cred.id)}
                onToggleDetails={onToggleDetails}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Main component
const OSINTEnrichmentPanel = ({ scanId: initialScanId, onEnrichmentComplete, workspaceId }) => {
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState(null);
  const [dataTypes, setDataTypes] = useState({});
  const [expandedCredentials, setExpandedCredentials] = useState(new Set());
  const [filter, setFilter] = useState('all'); // all, breached, high_risk
  const [showInternalOptions, setShowInternalOptions] = useState(false);
  const [internalDomain, setInternalDomain] = useState('');

  // Scan selection
  const [selectedScanId, setSelectedScanId] = useState(initialScanId || null);
  const [recentScans, setRecentScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);

  // Load recent scans for selection
  useEffect(() => {
    loadRecentScans();
  }, [workspaceId]);

  const loadRecentScans = async () => {
    setLoadingScans(true);
    try {
      const response = await scansApi.list(0, 20, workspaceId);
      const scans = response.scans || response || [];
      // Filter to completed scans
      const completedScans = scans.filter(s => s.status === 'completed' || s.status === 'finished');
      setRecentScans(completedScans);

      // Auto-select first scan if none selected
      if (!selectedScanId && completedScans.length > 0) {
        setSelectedScanId(completedScans[0].id);
      }
    } catch (error) {
      console.error('Failed to load scans:', error);
    } finally {
      setLoadingScans(false);
    }
  };

  // Load enrichment data when scan changes
  useEffect(() => {
    if (selectedScanId) {
      loadEnrichmentData();
    }
  }, [selectedScanId]);

  const loadEnrichmentData = async () => {
    setLoading(true);
    try {
      const [enrichment, types] = await Promise.all([
        osintApi.getScanEnrichment(selectedScanId),
        osintApi.getDataTypes()
      ]);

      setEnrichmentData(enrichment);
      setDataTypes(types.data_types || {});
    } catch (error) {
      console.error('Failed to load enrichment:', error);
      // Don't show error toast, just means no enrichment yet
    } finally {
      setLoading(false);
    }
  };

  // Run enrichment
  const runEnrichment = async (checkPasswords = false) => {
    setEnriching(true);
    try {
      const result = await osintApi.enrichScan(selectedScanId, checkPasswords);
      toast.success(`Enrichissement termine: ${result.enrichment?.enriched || 0} credentials enrichis`);
      await loadEnrichmentData();
      onEnrichmentComplete?.(result);
    } catch (error) {
      console.error('Enrichment failed:', error);
      toast.error('Erreur lors de l\'enrichissement OSINT');
    } finally {
      setEnriching(false);
    }
  };

  // Run internal scan enrichment
  const runInternalEnrichment = async () => {
    if (!internalDomain) {
      toast.error('Veuillez entrer un domaine');
      return;
    }

    setEnriching(true);
    try {
      const result = await osintApi.enrichInternalScan(selectedScanId, internalDomain);
      const criticalCount = result.critical_findings?.length || 0;
      if (criticalCount > 0) {
        toast.error(`${criticalCount} compte(s) admin compromis detecte(s)!`);
      } else {
        toast.success('Enrichissement interne termine');
      }
      await loadEnrichmentData();
      onEnrichmentComplete?.(result);
    } catch (error) {
      console.error('Internal enrichment failed:', error);
      toast.error('Erreur lors de l\'enrichissement interne');
    } finally {
      setEnriching(false);
    }
  };

  // Toggle credential details
  const toggleCredentialDetails = (credId) => {
    setExpandedCredentials(prev => {
      const newSet = new Set(prev);
      if (newSet.has(credId)) {
        newSet.delete(credId);
      } else {
        newSet.add(credId);
      }
      return newSet;
    });
  };

  // Filter credentials
  const filterCredentials = (credentials) => {
    if (filter === 'all') return credentials;

    return credentials.filter(cred => {
      if (filter === 'breached') {
        const hibp = cred.osint_results?.hibp || {};
        return hibp.breach_count > 0 || hibp.breaches?.length > 0;
      }
      if (filter === 'high_risk') {
        return (cred.risk_score || 0) >= 50;
      }
      return true;
    });
  };

  // Export results
  const exportResults = () => {
    if (!enrichmentData?.by_data_type) return;

    const exportData = {
      scan_id: selectedScanId,
      summary: enrichmentData.summary,
      by_data_type: enrichmentData.by_data_type,
      exported_at: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `osint_enrichment_${selectedScanId}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Resultats exportes');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Scan Selector */}
      <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <List className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-sm font-medium text-white">Selectionner un scan</p>
              <p className="text-xs text-gray-400">Choisissez un scan pour voir/lancer l'enrichissement OSINT</p>
            </div>
          </div>
          <select
            value={selectedScanId || ''}
            onChange={(e) => setSelectedScanId(e.target.value || null)}
            disabled={loadingScans}
            className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-sm text-white min-w-[300px]"
          >
            <option value="">-- Selectionner un scan --</option>
            {recentScans.map(scan => (
              <option key={scan.id} value={scan.id}>
                {scan.name || scan.target || `Scan ${scan.id.slice(0, 8)}`}
                {' - '}
                {new Date(scan.created_at).toLocaleDateString('fr-FR')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* No scan selected message */}
      {!selectedScanId && (
        <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-8 text-center">
          <Database className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">Aucun scan selectionne</h3>
          <p className="text-gray-400">
            Sélectionnez un scan ci-dessus pour voir ou lancer l'enrichissement OSINT
          </p>
        </div>
      )}

      {/* Main content - only show if scan is selected */}
      {selectedScanId && (
        <>
          {/* Header with actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-400" />
                Enrichissement OSINT
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Analyse automatique des credentials contre les bases de breaches
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value="all">Tous</option>
            <option value="breached">Breaches</option>
            <option value="high_risk">Haut risque</option>
          </select>

          {/* Export button */}
          {enrichmentData?.enriched && (
            <button
              onClick={exportResults}
              className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
            >
              <Download className="w-4 h-4" />
              Exporter
            </button>
          )}

          {/* Run enrichment */}
          <button
            onClick={() => runEnrichment(false)}
            disabled={enriching}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-sm transition-colors"
          >
            {enriching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {enrichmentData?.enriched ? 'Re-enrichir' : 'Lancer enrichissement'}
          </button>

          {/* Internal scan button */}
          <button
            onClick={() => setShowInternalOptions(!showInternalOptions)}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded-lg text-sm transition-colors"
          >
            <Users className="w-4 h-4" />
            AD/Interne
          </button>
        </div>
      </div>

      {/* Internal scan options */}
      {showInternalOptions && (
        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4">
          <h3 className="font-medium text-yellow-400 mb-2">Enrichissement Scan Interne (AD/Windows)</h3>
          <p className="text-sm text-gray-400 mb-3">
            Verifie les comptes AD/Windows contre HIBP en construisant l'email username@domaine
          </p>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={internalDomain}
              onChange={(e) => setInternalDomain(e.target.value)}
              placeholder="ex: exemple.com"
              className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400"
            />
            <button
              onClick={runInternalEnrichment}
              disabled={enriching || !internalDomain}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 rounded-lg text-sm transition-colors text-white"
            >
              {enriching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Analyser
            </button>
          </div>
        </div>
      )}

      {/* Summary stats */}
      {enrichmentData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <p className="text-2xl font-bold text-white">{enrichmentData.summary.total_credentials || 0}</p>
            <p className="text-sm text-gray-400">Total credentials</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <p className="text-2xl font-bold text-green-400">{enrichmentData.summary.enriched || 0}</p>
            <p className="text-sm text-gray-400">Enrichis</p>
          </div>
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <p className="text-2xl font-bold text-red-400">{enrichmentData.summary.high_risk || 0}</p>
            <p className="text-sm text-gray-400">Haut risque</p>
          </div>
          <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
            <p className="text-2xl font-bold text-yellow-400">{enrichmentData.summary.medium_risk || 0}</p>
            <p className="text-sm text-gray-400">Risque moyen</p>
          </div>
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <p className="text-2xl font-bold text-green-400">{enrichmentData.summary.low_risk || 0}</p>
            <p className="text-sm text-gray-400">Faible risque</p>
          </div>
        </div>
      )}

      {/* No enrichment yet */}
      {!enrichmentData?.enriched && (
        <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-8 text-center">
          <Database className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">Aucun enrichissement</h3>
          <p className="text-gray-400 mb-4">
            Lancez l'enrichissement OSINT pour analyser les credentials contre les bases de breaches
          </p>
          <button
            onClick={() => runEnrichment(false)}
            disabled={enriching}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {enriching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            Lancer l'analyse OSINT
          </button>
        </div>
      )}

      {/* Data type sections */}
      {enrichmentData?.by_data_type && (
        <div className="space-y-4">
          {Object.entries(enrichmentData.by_data_type)
            .filter(([_, data]) => filterCredentials(data.credentials || []).length > 0)
            .sort((a, b) => {
              // Sort by number of high-risk credentials
              const aHighRisk = (a[1].credentials || []).filter(c => c.risk_score >= 50).length;
              const bHighRisk = (b[1].credentials || []).filter(c => c.risk_score >= 50).length;
              return bHighRisk - aHighRisk;
            })
            .map(([dataType, data]) => (
              <DataTypeSection
                key={dataType}
                dataType={dataType}
                typeInfo={data.type_info || dataTypes[dataType] || {}}
                credentials={filterCredentials(data.credentials || [])}
                expandedCredentials={expandedCredentials}
                onToggleDetails={toggleCredentialDetails}
              />
            ))}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default OSINTEnrichmentPanel;
